import { axios } from '@/common/api.service.js'
import { getEffectiveItemType } from '@/helpers/getEffectiveItemType.js'
import hasTempId from '@/helpers/hasTempId.js'

function normaliseArtefactBaseItemTypeKey(type) {
  const rawType =
    typeof type === 'string'
      ? type
      : type && typeof type === 'object'
        ? type.name || type.short_name || type.label || ''
        : ''

  return rawType.trim().toLowerCase()
}

function isExpectedDelete404(error, parm) {
  return parm?.api_request?.method?.toUpperCase() === 'DELETE' && error?.response?.status === 404
}

function normaliseComparableValue(value) {
  return value === undefined ? null : value
}

function buildCharacterItemMatchLabel(item) {
  return String(item?.display_name ?? item?.name ?? '').trim()
}

function isTempStaticArtefactItem(item) {
  return Boolean(
    item?.type === 'Artefact' && (item?.create_static_item === true || hasTempId(item?.item)),
  )
}

function isMatchingTempCharacterItem(tempItem, persistedItem) {
  if (!tempItem || !persistedItem || !hasTempId(tempItem.id)) {
    return false
  }

  const tempCharacterId = tempItem.character ?? tempItem.character_id ?? null
  const persistedCharacterId = persistedItem.character ?? persistedItem.character_id ?? null

  if (
    tempCharacterId != null &&
    persistedCharacterId != null &&
    String(tempCharacterId) !== String(persistedCharacterId)
  ) {
    return false
  }

  const ignoreStaticItemIdMismatch =
    isTempStaticArtefactItem(tempItem) && persistedItem?.type === 'Artefact'

  if (!ignoreStaticItemIdMismatch) {
    if (
      tempItem.item != null &&
      persistedItem.item != null &&
      String(tempItem.item) !== String(persistedItem.item)
    ) {
      return false
    }
  }

  const tempLabel = buildCharacterItemMatchLabel(tempItem)
  const persistedLabel = buildCharacterItemMatchLabel(persistedItem)

  if (tempLabel && persistedLabel && tempLabel !== persistedLabel) {
    return false
  }

  const comparableKeys = [
    'type',
    'artefact_type',
    'tally_qty',
    'container',
    'worn_location',
    'attach_location',
    'armour_location',
    'held',
    'equipped',
    'player_notes',
    'short_description',
    'max_unit_count',
    'cur_unit_count',
    'pwr_storage',
  ]

  return comparableKeys.every((key) => {
    return normaliseComparableValue(tempItem[key]) === normaliseComparableValue(persistedItem[key])
  })
}

function buildCharacterRuneMatchLabel(rune) {
  return String(rune?.display_name ?? rune?.name ?? '').trim()
}

function isMatchingTempCharacterRune(tempRune, persistedRune) {
  if (!tempRune || !persistedRune || !hasTempId(tempRune.id)) {
    return false
  }

  const tempCharacterId = tempRune.character ?? tempRune.character_id ?? null
  const persistedCharacterId = persistedRune.character ?? persistedRune.character_id ?? null

  if (
    tempCharacterId != null &&
    persistedCharacterId != null &&
    String(tempCharacterId) !== String(persistedCharacterId)
  ) {
    return false
  }

  if (
    tempRune.rune != null &&
    persistedRune.rune != null &&
    String(tempRune.rune) !== String(persistedRune.rune)
  ) {
    return false
  }

  const tempLabel = buildCharacterRuneMatchLabel(tempRune)
  const persistedLabel = buildCharacterRuneMatchLabel(persistedRune)

  if (tempLabel && persistedLabel && tempLabel !== persistedLabel) {
    return false
  }

  const comparableKeys = [
    'strength',
    'pwr_storage',
    'starter',
    'integrated',
    'active',
    'character_item',
    'item_name',
    'trait',
  ]

  return comparableKeys.every((key) => {
    return normaliseComparableValue(tempRune[key]) === normaliseComparableValue(persistedRune[key])
  })
}

console.log('HERE CharacterJS')
//---------------
// set state
//---------------
const state = () => ({
  characters: [],
  abilities: [],
  skills: [],
  traits: [],
  items: [],
  currencies: [],
  item_sizes: [],
  container_sizes: [],
  artefact_types: [],
  item_lookups: {},
  character_lookups: {},
  artefact_base_items_by_type: {},
  available_campaign_artefacts: [],
  available_dropped_artefacts: [],
  runes: [],
  spells: [],
  starter_counts: [],
  current_character: null,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})
//=================================================
// getters
//---------
const getters = {
  //
  getStaticCommon(state) {
    let common = state.skills.filter((skill) => skill.type === 'Common')
    return common
  },
  getStaticUncommon(state) {
    let uncommon = state.skills.filter((skill) => skill.type === 'Uncommon')
    return uncommon
  },
  getStaticSpecialisms(state) {
    let specialisms = state.skills.filter((skill) => skill.type === 'Specialism')
    return specialisms
  },
  getStaticLanguages(state) {
    let languages = state.skills.filter((skill) => skill.type === 'Language')
    return languages
  },
  getStaticLores(state) {
    let lores = state.skills.filter((skill) => skill.type === 'Lore')
    return lores
  },
  getStaticMeleeWeapons(state) {
    let meleeWeapons = state.skills.filter((skill) => skill.type === 'Melee')
    return meleeWeapons
  },
  getStaticRangedWeapons(state) {
    let rangedWeapons = state.skills.filter((skill) => skill.type === 'Ranged')
    return rangedWeapons
  },
  getStaticCasting(state) {
    let casting = state.skills.filter(
      (skill) => skill.type === 'Sorcery' && skill.name.startsWith('Manipulation'),
    )
    return casting
  },
  getCharacters(state) {
    return state.characters
  },
  getStaticSkill: (state) => (id) => {
    let skill = state.skills.find((a) => a.id == id)
    try {
      return skill
    } catch {
      return null
    }
  },
  getStaticSkillByName: (state) => (name) => {
    let skill = state.skills.find((a) => a.name == name || a.display_name == name)
    try {
      return skill
    } catch {
      return null
    }
  },
  getTraits(state) {
    return state.traits
  },
  getStaticRune: (state) => (id) => {
    let rune = state.runes.find((a) => a.id == id)
    try {
      return rune
    } catch {
      return null
    }
  },
  getDefaultSkills(state) {
    let default_skills = state.skills.filter((skill) => skill.default === true)
    return default_skills
  },
  getSkill: (state) => (name) => {
    if (!name) {
      return state.skills
    } else {
      return state.skills.find((skill) => skill.name === name)
    }
  },
  getStarterCounts(state) {
    return state.starter_counts
  },
  getStarterItems(state) {
    let starter_items = state.items.filter((item) => item.starter === true)
    return starter_items
  },
  getStaticItemsByType: (state) => (type) => {
    if (!type || type.includes('All')) {
      return state.items
    } else {
      return state.items.filter((item) => item.type === type)
    }
  },
  getStaticActiveItemsByType: (state) => (type) => {
    if (!type || type.includes('All')) {
      return state.items.filter((item) => item.active === true)
    } else {
      const items = state.items.filter((item) => item.type === type && item.active === true)
      return items
    }
  },
  getStaticActiveItemsByEffectiveType: (state, getters) => (type) => {
    const getStaticItemById = getters.getStaticItemById
    const activeItems = state.items.filter((item) => item.active === true)

    if (!type || type.includes('All')) {
      return activeItems
    }

    return activeItems.filter((item) => getEffectiveItemType(item, getStaticItemById) === type)
  },
  getAvailableDroppedArtefacts(state) {
    return Array.isArray(state.available_dropped_artefacts) ? state.available_dropped_artefacts : []
  },
  getAvailableCampaignArtefacts(state) {
    return Array.isArray(state.available_campaign_artefacts)
      ? state.available_campaign_artefacts
      : []
  },
  getAvailableCampaignArtefactByName: (state) => (name) => {
    if (!name) {
      return null
    }

    return (
      Array.isArray(state.available_campaign_artefacts) ? state.available_campaign_artefacts : []
    ).find((item) => item?.name === name)
  },
  getAvailableDroppedArtefactByName: (state) => (name) => {
    if (!name) {
      return null
    }

    return (
      Array.isArray(state.available_dropped_artefacts) ? state.available_dropped_artefacts : []
    ).find((item) => item?.name === name)
  },
  getStaticItemByName: (state) => (name) => {
    if (!name) {
      return state.items
    } else {
      return (
        state.items.find((item) => item.name === name) ||
        (Array.isArray(state.available_campaign_artefacts)
          ? state.available_campaign_artefacts.find((item) => item?.name === name)
          : null) ||
        (Array.isArray(state.available_dropped_artefacts)
          ? state.available_dropped_artefacts.find((item) => item?.name === name)
          : null)
      )
    }
  },
  getStaticItemById: (state) => (id) => {
    if (!id) {
      return state.items
    } else {
      const staticItem = state.items.find((item) => item.id === id)

      if (staticItem) {
        return staticItem
      }

      const campaignArtefact = Array.isArray(state.available_campaign_artefacts)
        ? state.available_campaign_artefacts.find((item) => item?.id === id)
        : null

      if (campaignArtefact) {
        return campaignArtefact
      }

      const droppedArtefact = Array.isArray(state.available_dropped_artefacts)
        ? state.available_dropped_artefacts.find((item) => item?.id === id)
        : null

      if (droppedArtefact) {
        return droppedArtefact
      }

      const baseItemGroups = Object.values(state.artefact_base_items_by_type || {})
      for (const group of baseItemGroups) {
        const baseItem = Array.isArray(group) ? group.find((item) => item.id === id) : null

        if (baseItem) {
          return baseItem
        }
      }

      return undefined
    }
  },
  getArtefactBaseItemsByType: (state) => (type) => {
    const key = normaliseArtefactBaseItemTypeKey(type)
    return key ? state.artefact_base_items_by_type[key] || [] : []
  },
  getStaticSpellById: (state) => (id) => {
    if (!id) {
      return state.spells
    } else {
      return state.spells.find((spell) => spell.id === id)
    }
  },
  getStaticSpellsByType: (state) => (type) => {
    if (!type || type.includes('All')) {
      return state.spells
    } else {
      return state.spells.filter((spell) => spell.type === type)
    }
  },
  getStaticSpellByName: (state) => (name) => {
    if (!name) {
      return state.spells
    } else {
      return state.spells.find((spell) => spell.name === name)
    }
  },
  getStaticSpellsByRune: (state) => (rune_name) => {
    if (!rune_name) {
      return state.spells
    } else {
      return state.spells.filter((spell) => spell.casting_rune_name === rune_name)
    }
  },
  getRunes(state) {
    return state.runes
  },
  getItemLookups(state) {
    return state.item_lookups
  },
  getArtefactTypes(state) {
    console.log('IN getArtefactTypes getter = ', state.artefact_types)
    return state.artefact_types
  },
  getCharacterLookups(state) {
    return state.character_lookups
  },
  getCurrencies(state) {
    return state.currencies
  },
  getCurrencyByShortName: (state) => (shortName) => {
    console.log('IN getCurrencyByShortName getter = ', shortName)
    console.log('state.currencies = ', state.currencies)
    if (!shortName) {
      return state.currencies
    } else {
      return state.currencies.find((currency) => currency.short_name === shortName)
    }
  },
  getItemSizes(state) {
    return state.item_sizes
  },
  getContainerSizes(state) {
    return state.container_sizes
  },
  getContainerSizeByBaseItemId: (state) => (base_item_id) => {
    if (!base_item_id) {
      return state.items
    } else {
      return state.items.find((item) => item.id === base_item_id).container_size
    }
  },
  getRunesByType: (state) => (type) => {
    if (!type) {
      return state.runes
    } else {
      return state.runes.filter((rune) => rune.type === type)
    }
  },
  getCharacterSkills: (state) => (type) => {
    if (!type) {
      return state.current_character.skills
    } else {
      return state.current_character.skills.filter((skill) => skill.type === type)
    }
  },
  getCharacterNames(state) {
    if (state.characters && state.characters.length > 0) {
      const names_array = state.characters.map(function (array) {
        return array.name
      })
      return names_array
    }
    return null
  },
  getCurrentCharacter(state) {
    return state.current_character
  },
  getCharacter: (state) => (id) => {
    let character = state.characters.find((a) => a.id == id)
    try {
      return character
    } catch {
      return null
    }
  },
  getCharacterCharacteristics: (state) => (id) => {
    let character = state.characters.find((a) => a.id == id)
    try {
      return character.characteristics
    } catch {
      return null
    }
  },
  getAbility: (state) => (name) => {
    let ability = state.abilities.find((a) => a.name == name)
    try {
      return ability
    } catch {
      return null
    }
  },
  getHeaders(state) {
    return state.headers
  },
}
//---------------
// actions
//---------------
const actions = {
  //===============================================================
  // load static abilities
  //-----------------------
  async loadStaticAbilities({ commit, state }) {
    // console.log('-----------------------')
    if (Array.isArray(state.abilities) && state.abilities.length > 0) {
      return
    }
    console.log('JS loadStaticAbilities..')
    const endpoint = 'api/v1/abilities/'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setAbilities', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load static skills
  //-------------------
  async loadStaticSkills({ commit, state }, options = {}) {
    const forceReload =
      options === true || (options && typeof options === 'object' && options.force === true)

    if (!forceReload && Array.isArray(state.skills) && state.skills.length > 0) {
      return
    }
    // console.log('-----------------------')
    console.log('JS loadStaticSkills..')
    const endpoint = 'api/v1/skills/?limit=1000'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setSkills', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load static traits
  //-------------------
  async loadStaticTraits({ commit, state }) {
    console.log('JS loadStaticTraits..')

    if (Array.isArray(state.traits) && state.traits.length > 0) {
      return
    }
    const endpoint = 'api/v1/traits/?limit=1000'
    const headers = state.headers
    return axios
      .get(endpoint, {
        headers: headers,
        mode: 'same-origin',
      })
      .then((response) => {
        commit('setTraits', response.data.results)
      })
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load static runes
  //-------------------
  async loadStaticRunes({ commit, state }) {
    if (Array.isArray(state.runes) && state.runes.length > 0) {
      return
    }
    // console.log('-----------------------')
    console.log('JS loadStaticRunes..')
    const endpoint = 'api/v1/runes/?limit=1000'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setRunes', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load static spells
  //--------------------
  async loadStaticSpells({ commit, state }) {
    // console.log('-----------------------')
    if (Array.isArray(state.spells) && state.spells.length > 0) {
      return
    }
    console.log('JS loadStaticSpells..')
    const endpoint = 'api/v1/spells/?limit=1000'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setSpells', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load static currencies
  //------------------------
  async loadStaticCurrencies({ commit, state }) {
    if (Array.isArray(state.currencies) && state.currencies.length > 0) {
      return
    }
    // console.log('-----------------------')
    console.log('JS loadStaticCurrencies..')
    const endpoint = 'api/v1/currencies/?limit=1000'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setCurrencies', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load item sizes
  //-----------------
  async loadItemSizes({ commit, state }) {
    if (Array.isArray(state.item_sizes) && state.item_sizes.length > 0) {
      return
    }
    const endpoint = 'api/v1/item-sizes/'
    const headers = state.headers
    const itemSizes = []
    let nextUrl = endpoint

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers: headers,
          mode: 'same-origin',
        })

        const payload = response.data
        const pageResults = payload?.results ?? payload

        if (Array.isArray(pageResults)) {
          itemSizes.push(...pageResults)
        }

        nextUrl = payload?.next ?? null
      }

      commit('setItemSizes', itemSizes)
    } catch (err) {
      throw window.getErrorMessage(err)
    }
  },
  //===============================================================
  // load container sizes
  //----------------------
  async loadContainerSizes({ commit, state }) {
    if (Array.isArray(state.container_sizes) && state.container_sizes.length > 0) {
      return
    }
    const endpoint = 'api/v1/container-sizes/'
    const headers = state.headers
    const containerSizes = []
    let nextUrl = endpoint

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers: headers,
          mode: 'same-origin',
        })

        const payload = response.data
        const pageResults = payload?.results ?? payload

        if (Array.isArray(pageResults)) {
          containerSizes.push(...pageResults)
        }

        nextUrl = payload?.next ?? null
      }

      commit('setContainerSizes', containerSizes)
    } catch (err) {
      throw window.getErrorMessage(err)
    }
  },
  //===============================================================
  // load static starter counts
  //----------------------------
  async loadStaticStarterCounts({ commit, state }) {
    // console.log('-----------------------')
    if (Array.isArray(state.starter_counts) && state.starter_counts.length > 0) {
      return
    }
    console.log('JS loadStaticStarterCounts..')
    const endpoint = 'api/v1/starter-counts/'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setStarterCounts', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load static items
  //-------------------
  async loadStaticItems({ commit, state }) {
    // NB: deliberately not checking for existing items in state.items here
    // cos want to refresh list of items from the API each time this is called
    // in case new items have been added since the last load (i.e. artefacts)
    // console.log('-----------------------')
    console.log('JS loadStaticItems..')
    const endpoint = 'api/v1/items/?limit=1000'
    const headers = state.headers
    return (
      axios // NB: note 'return' and not 'await' here in order to allow Promise.all in caller
        .get(endpoint, {
          headers: headers,
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          commit('setItems', response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          throw window.getErrorMessage(err)
        })
    )
  },
  //===============================================================
  // load available dropped artefacts
  //---------------------------------
  async loadAvailableDroppedArtefacts({ commit, state }, characterId) {
    if (!characterId) {
      commit('setAvailableDroppedArtefacts', [])
      return []
    }

    const endpoint = `api/v1/character-items/dropped-artefacts/available/?character=${characterId}`
    const headers = state.headers

    return axios
      .get(endpoint, {
        headers: headers,
        mode: 'same-origin',
      })
      .then((response) => {
        const droppedArtefacts = Array.isArray(response.data) ? response.data : []
        commit('setAvailableDroppedArtefacts', droppedArtefacts)
        droppedArtefacts.forEach((item) => commit('setStaticItem', item))
        return droppedArtefacts
      })
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load available campaign artefacts
  //----------------------------------
  async loadAvailableCampaignArtefacts({ commit, state }, payload) {
    console.log('loadAvailableCampaignArtefacts: payload = ', payload)
    const request =
      payload && typeof payload === 'object'
        ? payload
        : { characterId: payload, arenaId: null, currentItemId: null }

    const characterId = request?.characterId ?? null
    const arenaId = request?.arenaId ?? null
    const currentItemId = request?.currentItemId ?? null

    console.log('loadAvailableCampaignArtefacts: characterId = ', characterId)
    console.log('loadAvailableCampaignArtefacts: arenaId = ', arenaId)
    console.log('loadAvailableCampaignArtefacts: currentItemId = ', currentItemId)

    if (!characterId && !arenaId) {
      console.warn('loadAvailableCampaignArtefacts: characterId and arenaId are missing or invalid')
      commit('setAvailableCampaignArtefacts', [])
      return []
    }

    const params = new URLSearchParams()
    if (characterId) {
      params.set('character', `${characterId}`)
    }
    if (arenaId) {
      params.set('arena', `${arenaId}`)
    }
    if (currentItemId) {
      params.set('current_item', `${currentItemId}`)
    }

    const endpoint = `api/v1/character-items/campaign-artefacts/available/?${params.toString()}`
    const headers = state.headers

    console.log('loadAvailableCampaignArtefacts: endpoint = ', endpoint)
    return axios
      .get(endpoint, {
        headers: headers,
        mode: 'same-origin',
      })
      .then((response) => {
        const campaignArtefacts = Array.isArray(response.data) ? response.data : []
        commit('setAvailableCampaignArtefacts', campaignArtefacts)
        campaignArtefacts.forEach((item) => commit('setStaticItem', item))
        return campaignArtefacts
      })
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load artefact base items
  //-------------------------
  async loadArtefactBaseItems({ commit, state }, artefactType) {
    const key = normaliseArtefactBaseItemTypeKey(artefactType)
    if (!key) {
      return []
    }

    if (Object.prototype.hasOwnProperty.call(state.artefact_base_items_by_type, key)) {
      return state.artefact_base_items_by_type[key]
    }

    const endpoint = `api/v1/items/base-items/?artefact_type=${encodeURIComponent(artefactType)}`
    const headers = state.headers
    const items = []
    let nextUrl = endpoint

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers: headers,
          mode: 'same-origin',
        })

        const payload = response.data
        const pageResults = payload?.results ?? payload

        if (Array.isArray(pageResults)) {
          items.push(...pageResults)
        }

        nextUrl = payload?.next ?? null
      }

      commit('setArtefactBaseItems', {
        artefactType: key,
        items,
      })

      return items
    } catch (err) {
      throw window.getErrorMessage(err)
    }
  },
  //===============================================================
  // load character lookups (choices)
  //-----------------------------
  async loadCharacterLookups({ commit, state }) {
    const endpoint = 'api/v1/characters/lookups/'
    const headers = state.headers
    return axios
      .get(endpoint, {
        headers: headers,
        mode: 'same-origin',
      })
      .then((response) => {
        commit('setCharacterLookups', response.data)
      })
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load item lookups (choices)
  //-----------------------------
  async loadItemLookups({ commit, state }) {
    if (
      state.item_lookups &&
      typeof state.item_lookups === 'object' &&
      Object.keys(state.item_lookups).length > 0 &&
      Array.isArray(state.item_lookups.count_unit) &&
      state.item_lookups.count_unit.includes('Unknown')
    ) {
      return
    }
    const endpoint = 'api/v1/items/lookups/'
    const headers = state.headers
    return axios
      .get(endpoint, {
        headers: headers,
        mode: 'same-origin',
      })
      .then((response) => {
        commit('setItemLookups', response.data)
      })
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load artefact types
  //-------------------
  async loadArtefactTypes({ commit, state }) {
    if (Array.isArray(state.artefact_types) && state.artefact_types.length > 0) {
      return
    }
    const endpoint = 'api/v1/items/artefact-types/'
    const headers = state.headers
    const artefactTypes = []
    let nextUrl = endpoint

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers: headers,
          mode: 'same-origin',
        })

        const payload = response.data
        const pageResults = payload?.results ?? payload

        if (Array.isArray(pageResults)) {
          artefactTypes.push(...pageResults)
        }

        nextUrl = payload?.next ?? null
      }

      commit('setArtefactTypes', artefactTypes)
    } catch (err) {
      throw window.getErrorMessage(err)
    }
  },
  //===============================================================
  // load campaign characters
  //-----------------------
  async loadCampaignCharacters({ commit, state }, slug) {
    // console.log("-----------------------");
    // console.log("IN CHARACTERS JS loadCampaignCharacters");
    const endpoint = 'api/v1/campaigns/' + slug + '/characters/'
    const headers = state.headers
    await axios
      .get(endpoint, {
        headers: { headers },
        mode: 'same-origin',
      })
      //--------------
      // where api ok
      //--------------
      .then((response) => {
        commit('setCharacters', response.data.results)
      })
      //-----------------
      // where api error
      //-----------------
      .catch((err) => {
        throw window.getErrorMessage(err)
      })
  },
  //===============================================================
  // load my characters
  //-------------------
  async loadMyCharacters({ commit, state }) {
    const headers = state.headers
    const characters = []
    let nextUrl = 'api/v1/characters/my/'

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl, {
          headers: { headers },
          mode: 'same-origin',
        })

        const payload = response.data
        const pageResults = payload?.results ?? payload

        if (Array.isArray(pageResults)) {
          characters.push(...pageResults)
        }

        nextUrl = payload?.next ?? null
      }

      commit('setCharacters', characters)
    } catch (err) {
      throw window.getErrorMessage(err)
    }
  },
  //===============================================================
  // load campaign deployable characters
  //-------------------------------------
  async loadCampaignDeployableCharacters({ state }, slug) {
    const endpoint = 'api/v1/campaigns/' + slug + '/characters/deploy/'
    const headers = state.headers
    return new Promise((resolve, reject) => {
      axios
        .get(endpoint, {
          headers: { headers },
          mode: 'same-origin',
        })
        //--------------
        // where api ok
        //--------------
        .then((response) => {
          // console.log("CHARACTERS ... response")
          // window.displayResponseData(response.data.results)
          resolve(response.data.results)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          reject(err)
        })
    })
  },
  //===============================================================
  // edit character (add/change)
  //-----------------------------
  editCharacter({ state }, parm) {
    // console.log('-------------------------')
    // console.log('IN editCharacter')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          console.log('IN editCharacter got response')
          resolve(response)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((err) => {
          console.log('IN editCharacter got error')
          console.log('editCharacter status =', err?.response?.status)
          console.log('editCharacter response data =', err?.response?.data)
          console.log('editCharacter payload =', parm?.payload)
          reject(err)
        })
    })
  },
  //===============================================================
  // edit character characteristic (add/change)
  //--------------------------------------------
  editCharacterCharacteristic({ state }, parm) {
    // console.log("---------------------");
    // console.log("IN editCharacterCharacteristic....");
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character armour location (patch)
  //---------------------------------------
  editCharacterArmourLocation({ state }, parm) {
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        .then((response) => {
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  //============================================================================
  // edit character adjustment (add/change/delete) e.g. characteristic, ability
  //----------------------------------------------------------------------------
  editCharacterAdj({ state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterAdj....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          console.error('editCharacterRune Vuex action error:', error)
          if (error && error.response) {
            console.error('editCharacterRune response data:', error.response.data)
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // delete character
  //------------------
  deleteCharacter(_, request) {
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: request.method,
        url: request.endpoint,
        headers: headers,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          reject(error)
        })
    })
  },
  //===============================================================
  // change character image (patch)
  //--------------------------------
  changeCharacterImage(_, parm) {
    // console.log('--------------------------')
    // console.log('IN changeCharacterImage')
    const endpoint = 'api/v1/characters/' + parm.slug + '/image/'

    return new Promise((resolve, reject) => {
      axios({
        method: 'patch',
        url: endpoint,
        headers: { 'content-type': 'multipart/form-data' },
        data: parm.image_file,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          // console.log('BACK FROM changeCharacterImage')
          resolve(response.data)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          // console.log('changeCharacterImage error')
          reject(error)
        })
    })
  },
  //===============================================================
  // change character history (patch)
  //----------------------------------
  changeCharacterHistory(_, parm) {
    const endpoint = 'api/v1/characters/' + parm.slug + '/history/'

    return new Promise((resolve, reject) => {
      axios({
        method: 'patch',
        url: endpoint,
        headers: { 'content-type': 'multipart/form-data' },
        data: parm.character_history_file,
        mode: 'same-origin',
      })
        .then((response) => {
          resolve(response.data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  //===============================================================
  // create artefact item (Item + CharacterItem)
  //--------------------------------------------
  createCharacterArtefactItem({ commit, state }, formData) {
    console.log('-------------------------------')
    console.log('IN createCharacterArtefactItem')
    const endpoint = 'api/v1/character-items/artefact-create/'

    // console.log('createCharacterArtefactItem formData...')
    // for (const [key, value] of formData.entries()) {
    //   console.log(key, value)
    // }

    return new Promise((resolve, reject) => {
      const baseHeaders = state.headers || {}
      const headers = { ...baseHeaders, 'content-type': 'multipart/form-data' }

      axios({
        method: 'post',
        url: endpoint,
        headers: headers,
        data: formData,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          console.log('BACK FROM createCharacterArtefactItem')
          // mirror editCharacterItem behaviour and update the
          // character item cache where this is not a preview call
          if (response && response.data) {
            commit('setCharacterItem', response.data)
          }
          resolve(response)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          console.error('createCharacterArtefactItem error:', error)
          if (error && error.response) {
            console.error('createCharacterArtefactItem response data:', error.response.data)
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character skill (crud)
  //-----------------------------
  editCharacterSkill({ state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterSkill....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterSkill...')
          //--------------------------------------------
          // add/update skill where not in preview mode
          //--------------------------------------------
          // if (!parm.api_preview) {
          //   commit('setCharacterSkill', response.data)
          // }
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          if (!isExpectedDelete404(error, parm)) {
            console.error('editCharacterSkill Vuex action error:', error)
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // delete character skill (hard delete)
  //-------------------------------------
  deleteCharacterSkill({ commit, state }, id_request) {
    console.log('---------------------')
    console.log('IN deleteCharacterSkill....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: id_request.request.method,
        url: id_request.request.endpoint,
        headers: headers,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          //---------------------------------
          // remove skill from current state
          //---------------------------------
          commit('removeCharacterSkill', id_request.id)
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          console.error('deleteCharacterSkill Vuex action error:', error)
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character item (crud)
  //----------------------------
  editCharacterItem({ commit, state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterItem....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterItem...')
          //-------------------------------------------
          // add/update item where not in preview mode. DELETE
          // operations typically return 204/empty bodies, so
          // skip committing in that case to avoid noisy
          // warnings from setCharacterItem.
          //-------------------------------------------
          const method = (parm.api_request && parm.api_request.method) || 'get'
          if (!parm.api_preview && method.toLowerCase() !== 'delete' && response.data) {
            const mergedPayload = { ...response.data }

            // Some character-item updates (e.g. enc) can be returned stale
            // by the API response. Preserve the value that was just sent so
            // the frontend state does not revert on commit.
            if (parm.payload && Object.prototype.hasOwnProperty.call(parm.payload, 'enc')) {
              mergedPayload.enc = parm.payload.enc

              const encNum = Number(mergedPayload.enc)
              const qtyNum = Number(mergedPayload.tally_qty ?? parm.payload.tally_qty ?? 0)
              if (!Number.isNaN(encNum) && !Number.isNaN(qtyNum)) {
                mergedPayload.tot_enc = Math.round((encNum * qtyNum + Number.EPSILON) * 100) / 100
              }
            }

            ;['ap', 'hp', 'pwr_storage'].forEach((key) => {
              if (parm.payload && Object.prototype.hasOwnProperty.call(parm.payload, key)) {
                mergedPayload[key] = parm.payload[key]
              }
            })

            commit('setCharacterItem', mergedPayload)
          }
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          // propagate the actual error object so callers can inspect it
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character rune (crud)
  //----------------------------
  editCharacterRune({ commit, state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterRune....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterRune...')
          //------------------------------------------------------
          // On DELETE, remove the rune locally by id. Upserting
          // the response payload can create a ghost blank row when
          // the API returns no body for a successful delete.
          //------------------------------------------------------
          if (!parm.api_preview) {
            if (String(parm?.api_request?.method || '').toLowerCase() === 'delete') {
              commit('removeCharacterRune', parm?.api_request?.id)
            } else {
              commit('setCharacterRune', response.data)
            }
          }
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          if (!isExpectedDelete404(error, parm)) {
            console.error('editCharacterRune Vuex action error:', error)
            if (error && error.response) {
              console.error('editCharacterRune response data:', error.response.data)
            }
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character spell (crud)
  //----------------------------
  editCharacterSpell({ state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterSpell....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterSpell...')
          //-------------------------------------------------
          // For now we do not mutate frontend character.spells
          // here; spells are derived from effective-spells on
          // reload. Callers already update in-memory spells.
          //-------------------------------------------------
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          if (!isExpectedDelete404(error, parm)) {
            console.error('editCharacterSpell Vuex action error:', error)
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character count (crud)
  //----------------------------
  editCharacterCount({ commit, state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterCount....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterCount...')
          //------------------------------------------------------
          // On DELETE, remove the count locally by id. Upserting
          // the response payload can create a ghost blank row when
          // the API returns no body for a successful delete.
          //------------------------------------------------------
          if (!parm.api_preview) {
            if (String(parm?.api_request?.method || '').toLowerCase() === 'delete') {
              commit('removeCharacterCount', parm?.api_request?.id)
            } else {
              commit('setCharacterCount', response.data)
            }
          }
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          console.error('editCharacterCount Vuex action error:', error)
          if (error && error.response) {
            console.error('editCharacterCount response data:', error.response.data)
          }
          reject(error)
        })
    })
  },
  //===============================================================
  // edit character campaign note (crud)
  //-------------------------------------
  editCharacterCampaignNote({ commit, state }, parm) {
    console.log('---------------------')
    console.log('IN editCharacterCampaignNote....')
    return new Promise((resolve, reject) => {
      const headers = state.headers
      axios({
        method: parm.api_request.method,
        url: parm.api_request.endpoint,
        headers: headers,
        data: parm.payload,
        mode: 'same-origin',
      })
        //----------
        // where ok
        //----------
        .then((response) => {
          resolve(response)
          console.log('BACK FROM editCharacterCampaignNote...')
          //-----------------------------------------------------------
          // On DELETE, remove the note locally by id. Upserting the
          // response payload can create a ghost blank row when the API
          // returns no body for a successful delete.
          //-----------------------------------------------------------
          if (!parm.api_preview) {
            if (String(parm?.api_request?.method || '').toLowerCase() === 'delete') {
              commit('removeCharacterCampaignNote', parm?.api_request?.id)
            } else {
              commit('setCharacterCampaignNote', response.data)
            }
          }
        })
        //-----------------
        // where api error
        //-----------------
        .catch((error) => {
          console.error('editCharacterCampaignNote Vuex action error:', error)
          if (error && error.response) {
            console.error('editCharacterCampaignNote response data:', error.response.data)
          }
          reject(error)
        })
    })
  },
}
//===============================================================
// mutations
//-----------
const mutations = {
  // ---------------------------------
  //   addTempStaticItem(state, payload) {
  //   console.log('IN addTempStaticItem mutation payload = ', payload)
  //   state.items.push(payload)
  // },
  // ---------------------------------
  setCharacters(state, payload) {
    state.characters = payload
  },
  // ---------------------------------
  setCurrentCharacter(state, payload) {
    state.current_character = payload
  },
  // ---------------------------------
  resetCurrentCharacter(state) {
    state.current_character = null
  },
  // ---------------------------------
  setStarterCounts(state, payload) {
    state.starter_counts = payload
  },
  // ---------------------------------
  setSkills(state, payload) {
    state.skills = payload
  },
  // ---------------------------------
  setTraits(state, payload) {
    state.traits = payload
  },
  // ---------------------------------
  setItems(state, payload) {
    state.items = payload
  },
  // ---------------------------------
  setAvailableCampaignArtefacts(state, payload) {
    state.available_campaign_artefacts = Array.isArray(payload) ? payload : []
  },
  // ---------------------------------
  setAvailableDroppedArtefacts(state, payload) {
    state.available_dropped_artefacts = Array.isArray(payload) ? payload : []
  },
  // ---------------------------------
  setStaticItem(state, payload) {
    if (!payload || !payload.id) {
      return
    }

    if (!Array.isArray(state.items)) {
      state.items = []
    }

    const idx = state.items.findIndex((item) => item.id === payload.id)

    if (idx !== -1) {
      state.items[idx] = {
        ...state.items[idx],
        ...payload,
      }
    } else {
      state.items.push(payload)
    }
  },
  // ---------------------------------
  setCurrencies(state, payload) {
    state.currencies = payload
  },
  // ---------------------------------
  setItemSizes(state, payload) {
    state.item_sizes = payload
  },
  // ---------------------------------
  setContainerSizes(state, payload) {
    state.container_sizes = payload
  },
  // ---------------------------------
  setArtefactTypes(state, payload) {
    state.artefact_types = payload
  },
  // ---------------------------------
  setCharacterLookups(state, payload) {
    state.character_lookups = payload
  },
  // ---------------------------------
  setItemLookups(state, payload) {
    state.item_lookups = payload
  },
  // ---------------------------------
  setArtefactBaseItems(state, payload) {
    if (!payload?.artefactType) {
      return
    }

    state.artefact_base_items_by_type = {
      ...state.artefact_base_items_by_type,
      [payload.artefactType]: Array.isArray(payload.items) ? payload.items : [],
    }
  },
  // ---------------------------------
  setSpells(state, payload) {
    state.spells = payload
  },
  // ---------------------------------
  setAbilities(state, payload) {
    state.abilities = payload
  },
  // ---------------------------------
  setRunes(state, payload) {
    state.runes = payload
  },
  // ---------------------------------
  setCharacterSkill(state, payload) {
    // Ensure we have a current character; if not, try to resolve it
    // using the information on the payload.
    if (!state.current_character) {
      let resolvedCharacter = null

      // A. Prefer an explicit character id on the payload (used when
      // creating new character skills – character id is required).
      const characterId = payload && (payload.character || payload.character_id)
      if (characterId) {
        resolvedCharacter = state.characters.find((c) => c.id === characterId)
      }

      // B. Fallback: for existing skills (PATCH/DELETE-style flows),
      // try to locate the owning character via the character-skill id.
      if (!resolvedCharacter && payload && payload.id != null) {
        resolvedCharacter = state.characters.find(
          (c) => Array.isArray(c.skills) && c.skills.some((s) => s.id === payload.id),
        )
      }

      if (resolvedCharacter) {
        state.current_character = resolvedCharacter
      } else {
        console.warn(
          'setCharacterSkill could not resolve a character for payload; skipping state update.',
          payload,
        )
        return
      }
    }

    if (!state.current_character.skills) {
      state.current_character.skills = []
    }
    // ------------------------
    // find skill index for id
    const idx = state.current_character.skills.findIndex((s) => s.id === payload.id)
    if (idx !== -1) {
      // --------------
      // replace skill
      state.current_character.skills[idx] = payload
    } else {
      // ----------
      // add skill
      state.current_character.skills.push(payload)
    }
  },
  // ---------------------------------
  setCharacterItem(state, payload) {
    // Ensure we have a current character; if not, try to resolve it
    // using the information on the payload.
    if (!state.current_character) {
      let resolvedCharacter = null

      // A. Prefer an explicit character id on the payload.
      const characterId = payload && (payload.character || payload.character_id)
      if (characterId) {
        resolvedCharacter = state.characters.find((c) => c.id === characterId)
      }

      // B. Fallback: for existing items, try to locate the owning
      // character via the character-item id.
      if (!resolvedCharacter && payload && payload.id != null) {
        resolvedCharacter = state.characters.find(
          (c) => Array.isArray(c.items) && c.items.some((i) => i.id === payload.id),
        )
      }

      if (resolvedCharacter) {
        state.current_character = resolvedCharacter
      } else {
        console.warn(
          'setCharacterItem could not resolve a character for payload; skipping state update.',
          payload,
        )
        return
      }
    }

    if (!state.current_character.items) {
      state.current_character.items = []
    }
    // ------------------------
    // find item index for id
    const idx = state.current_character.items.findIndex((s) => s.id === payload.id)
    if (idx !== -1) {
      // --------------
      // replace item
      state.current_character.items[idx] = payload
    } else {
      const tempIdx = state.current_character.items.findIndex((item) =>
        isMatchingTempCharacterItem(item, payload),
      )

      if (tempIdx !== -1) {
        state.current_character.items[tempIdx] = {
          ...state.current_character.items[tempIdx],
          ...payload,
        }
      } else {
        // ----------
        // add item
        state.current_character.items.push(payload)
      }
    }
  },
  // ---------------------------------
  setCharacterRune(state, payload) {
    if (!state.current_character.runes) {
      state.current_character.runes = []
    }
    // -----------------------
    // find rune index for id
    const idx = state.current_character.runes.findIndex((s) => s.id === payload.id)
    if (idx !== -1) {
      // -------------
      // replace rune
      state.current_character.runes[idx] = payload
    } else {
      const tempIdx = state.current_character.runes.findIndex((rune) =>
        isMatchingTempCharacterRune(rune, payload),
      )

      if (tempIdx !== -1) {
        state.current_character.runes[tempIdx] = {
          ...state.current_character.runes[tempIdx],
          ...payload,
        }
      } else {
        // ---------
        // add rune
        state.current_character.runes.push(payload)
      }
    }
  },
  // ---------------------------------
  setCharacterCount(state, payload) {
    if (!state.current_character.counts) {
      state.current_character.counts = []
    }
    // -----------------------
    // find count index for id
    const idx = state.current_character.counts.findIndex((s) => s.id === payload.id)
    if (idx !== -1) {
      // -------------
      // replace count
      state.current_character.counts[idx] = payload
    } else {
      // ---------
      // add count
      state.current_character.counts.push(payload)
    }
  },
  // ---------------------------------
  removeCharacterCount(state, id) {
    if (!state.current_character?.counts) {
      return
    }

    const idx = state.current_character.counts.findIndex(
      (count) => String(count?.id) === String(id),
    )

    if (idx !== -1) {
      state.current_character.counts.splice(idx, 1)
    }
  },
  // ---------------------------------
  setCharacterCampaignNote(state, payload) {
    if (!state.current_character.campaign_notes) {
      state.current_character.campaign_notes = []
    }
    // -----------------------
    // find campaign note index for id
    const idx = state.current_character.campaign_notes.findIndex((s) => s.id === payload.id)
    if (idx !== -1) {
      // -------------
      // replace campaign note
      state.current_character.campaign_notes[idx] = payload
    } else {
      // ---------
      // add campaign note
      state.current_character.campaign_notes.push(payload)
    }
  },
  // ---------------------------------
  removeCharacterCampaignNote(state, id) {
    if (!state.current_character?.campaign_notes) {
      return
    }

    const idx = state.current_character.campaign_notes.findIndex(
      (campaignNote) => String(campaignNote?.id) === String(id),
    )

    if (idx !== -1) {
      state.current_character.campaign_notes.splice(idx, 1)
    }
  },
  // ---------------------------------
  removeCharacterSkill(state, id) {
    // ------------------------
    // find skill index for id
    const idx = state.current_character.skills.findIndex((s) => s.id === id)
    if (idx !== -1) {
      // -------------
      // remove skill
      state.current_character.skills.splice(idx, 1)
    }
  },
  // ----------------------------------
  removeCharacterRune(state, id) {
    if (!state.current_character?.runes) {
      return
    }

    const idx = state.current_character.runes.findIndex((rune) => String(rune?.id) === String(id))

    if (idx !== -1) {
      state.current_character.runes.splice(idx, 1)
    }
  },
}
//===============================================================
// export
//--------
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
